// src/components/Admin/Admin.js
import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Alert,
  CircularProgress,
  useMediaQuery,
  Card,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Grid,
  Stack,
} from "@mui/material";
import {
  Lock as LockIcon,
  SensorDoorOutlined as DoorIcon,
  LockOpen as LockOpenIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  MonetizationOn as MonetizationOnIcon,
  Payment as PaymentIcon,
  SupportAgentOutlined as SupportIcon,
  CircleNotificationsOutlined as SettingsIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { database } from "../../firebase"; // Adjust the path as necessary
import { ref, onValue } from "firebase/database";
import { useTheme, styled } from "@mui/material/styles";
import TopBar from "../Sidebar/SidebarAdmin"; // Adjust the import path as necessary
// Styled Components for Flip Animation within MUI Card
const FlipCardContainer = styled(Box)(({ theme }) => ({
  perspective: 1000,
  height: "100%", // Ensure the container takes full height of the card
}));

const FlipCardInner = styled(Box)(({ theme, isflipped }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  textAlign: "center",
  transition: "transform 0.6s",
  transformStyle: "preserve-3d",
  transform: isflipped === "true" ? "rotateY(180deg)" : "rotateY(0deg)",
}));

const FlipCardFace = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: "100%",
  height: "100%",
  backfaceVisibility: "hidden",
  borderRadius: theme.shape.borderRadius,
  boxSizing: "border-box",
}));

const FlipCardFront = styled(FlipCardFace)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const FlipCardBack = styled(FlipCardFace)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
  color: theme.palette.primary.contrastText,
  transform: "rotateY(180deg)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center", // Center content horizontally
  justifyContent: "center", // Center content vertically
  padding: theme.spacing(3),
  overflowY: "auto", // Allow scrolling if content overflows
}));

const Stats = () => {
  // State variables
  const [lockers, setLockers] = useState({});
  const [users, setUsers] = useState({});
  const [supportRequests, setSupportRequests] = useState({});
  const [notifications, setNotifications] = useState({});
  const [keyMetrics, setKeyMetrics] = useState({
    totalLockers: 0,
    occupiedLockers: 0,
    availableLockers: 0,
    totalUsers: 0,
    newUsersThisMonth: 0,
    totalOverdueFees: 0,
    totalPaymentsGenerated: 0,
    pendingSupportRequests: 0,
    activeNotifications: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flippedCards, setFlippedCards] = useState({}); // Track flipped state per card

  const theme = useTheme(); // Ensure ThemeProvider wraps this component
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch data from Firebase
  useEffect(() => {
    const lockersRef = ref(database, "lockers");
    const usersRef = ref(database, "users");
    const supportRequestsRef = ref(database, "supportRequests");
    const notificationsRef = ref(database, "notifications");

    // Lockers
    onValue(
      lockersRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        setLockers(data);
      },
      (error) => {
        console.error("Error fetching lockers:", error);
        setError("Failed to load lockers data.");
      }
    );

    // Users
    onValue(
      usersRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        setUsers(data);
      },
      (error) => {
        console.error("Error fetching users:", error);
        setError("Failed to load users data.");
      }
    );

    // Support Requests
    onValue(
      supportRequestsRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        setSupportRequests(data);
      },
      (error) => {
        console.error("Error fetching support requests:", error);
        setError("Failed to load support requests.");
      }
    );

    // Notifications
    onValue(
      notificationsRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        setNotifications(data);
      },
      (error) => {
        console.error("Error fetching notifications:", error);
        setError("Failed to load notifications.");
      }
    );
  }, []);

  // Function to calculate overdue fee for a single locker
  const calculateOverdueFee = (expiryTime) => {
    if (!expiryTime) return 0;
    const now = new Date();
    const expiry = new Date(expiryTime);
    const diffInMs = now - expiry;

    if (diffInMs <= 0) return 0; // Not overdue

    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const feeIntervals = Math.ceil(diffInMinutes / 5); // 1 peso per 5 minutes
    const fee = feeIntervals * 1; // 1 peso per interval

    return fee;
  };

  // Compute Key Metrics
  useEffect(() => {
    if (
      Object.keys(lockers).length === 0 &&
      Object.keys(users).length === 0 &&
      Object.keys(supportRequests).length === 0 &&
      Object.keys(notifications).length === 0
    ) {
      return;
    }

    // Total Lockers
    const totalLockers = Object.keys(lockers).length;

    // Occupied Lockers
    const occupiedLockers = Object.values(lockers).filter(
      (locker) => locker.availability === "occupied"
    ).length;

    // Available Lockers
    const availableLockers = totalLockers - occupiedLockers;

    // Total Users
    const totalUsers = Object.keys(users).length;

    // New Users This Month
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // Months are 0-based
    const currentYear = currentDate.getFullYear();

    const newUsersThisMonth = Object.values(users).filter((user) => {
      const regDate = new Date(user.registration_date);
      return (
        regDate.getMonth() + 1 === currentMonth &&
        regDate.getFullYear() === currentYear
      );
    }).length;

    // Total Overdue Fees
    const totalOverdueFees = Object.values(lockers).reduce((acc, locker) => {
      if (locker.availability === "occupied" && locker.expiryTime) {
        const fee = calculateOverdueFee(locker.expiryTime);
        return acc + fee;
      }
      return acc;
    }, 0);

    // Total Payments Generated
    const totalPaymentsGenerated = Object.values(lockers).reduce(
      (acc, locker) => acc + (locker.paymentGenerated || 0),
      0
    );

    // Pending Support Requests
    const pendingSupportRequests = Object.keys(supportRequests).length;

    // Active Notifications
    const activeNotifications = Object.keys(notifications).length;

    setKeyMetrics({
      totalLockers,
      occupiedLockers,
      availableLockers,
      totalUsers,
      newUsersThisMonth,
      totalOverdueFees,
      totalPaymentsGenerated,
      pendingSupportRequests,
      activeNotifications,
    });

    setLoading(false);
  }, [lockers, users, supportRequests, notifications]);

  // Optional: Update overdue fees every minute to keep them current
  useEffect(() => {
    const interval = setInterval(() => {
      // Trigger re-computation of key metrics by updating lockers state
      setLockers((prevLockers) => ({ ...prevLockers }));
    }, 60000); // 60,000 ms = 1 minute

    return () => clearInterval(interval);
  }, []);

  // Helper Functions for Detailed Data (No changes needed)
  const getOccupiedLockersDetails = () => {
    const occupied = Object.entries(lockers).filter(
      ([_, locker]) => locker.availability === "occupied"
    );
    if (occupied.length === 0) return "No lockers are currently occupied.";

    return (
      <List dense>
        {occupied.map(([lockerId, locker]) => (
          <Box key={lockerId}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Typography variant="subtitle1" fontWeight="bold">
                    Locker ID: {lockerId}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="textSecondary">
                      Assigned To: {locker.assignedTo || "N/A"}
                    </Typography>
                    <br />
                    <Typography component="span" variant="body2" color="textSecondary">
                      Expiry Time:{" "}
                      {locker.expiryTime
                        ? new Date(locker.expiryTime).toLocaleString()
                        : "N/A"}
                    </Typography>
                  </>
                }
              />
            </ListItem>
            <Divider component="li" />
          </Box>
        ))}
      </List>
    );
  };

  const getAvailableLockersDetails = () => {
    const available = Object.entries(lockers).filter(
      ([_, locker]) => locker.availability !== "occupied"
    );
    if (available.length === 0) return "No lockers are currently available.";

    return (
      <List dense>
        {available.map(([lockerId, locker]) => (
          <Box key={lockerId}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Typography variant="subtitle1" fontWeight="bold">
                    Locker ID: {lockerId}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="textSecondary">
                      Status: {locker.status || "N/A"}
                    </Typography>
                    <br />
                    <Typography component="span" variant="body2" color="textSecondary">
                      Payment Generated: ₱{locker.paymentGenerated || 0}
                    </Typography>
                  </>
                }
              />
            </ListItem>
            <Divider component="li" />
          </Box>
        ))}
      </List>
    );
  };

  const getTotalUsersDetails = () => {
    if (keyMetrics.totalUsers === 0) return "No users found.";

    return (
      <List dense>
        {Object.entries(users).map(([userId, user]) => (
          <Box key={userId}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Typography variant="subtitle1" fontWeight="bold">
                    {user.first_name} {user.last_name}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="textSecondary">
                      Email: {user.email}
                    </Typography>
                    <br />
                    <Typography component="span" variant="body2" color="textSecondary">
                      Organization: {user.organization}
                    </Typography>
                  </>
                }
              />
            </ListItem>
            <Divider component="li" />
          </Box>
        ))}
      </List>
    );
  };

  const getNewUsersDetails = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    const newUsers = Object.entries(users).filter(([_, user]) => {
      const regDate = new Date(user.registration_date);
      return (
        regDate.getMonth() + 1 === currentMonth &&
        regDate.getFullYear() === currentYear
      );
    });

    if (newUsers.length === 0) return "No new users this month.";

    return (
      <List dense>
        {newUsers.map(([userId, user]) => (
          <Box key={userId}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Typography variant="subtitle1" fontWeight="bold">
                    {user.first_name} {user.last_name}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="textSecondary">
                      Registered On:{" "}
                      {user.registration_date
                        ? new Date(user.registration_date).toLocaleString()
                        : "N/A"}
                    </Typography>
                  </>
                }
              />
            </ListItem>
            <Divider component="li" />
          </Box>
        ))}
      </List>
    );
  };

  const getOverdueFeesDetails = () => {
    const lockersWithFees = Object.entries(lockers).filter(
      ([_, locker]) => locker.overdueFee > 0
    );

    if (lockersWithFees.length === 0) return "No overdue fees currently.";

    return (
      <List dense>
        {lockersWithFees.map(([lockerId, locker]) => (
          <Box key={lockerId}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Typography variant="subtitle1" fontWeight="bold">
                    Locker ID: {lockerId}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="textSecondary">
                      Overdue Fee: ₱{locker.overdueFee}
                    </Typography>
                    <br />
                    <Typography component="span" variant="body2" color="textSecondary">
                      Assigned To: {locker.assignedTo || "N/A"}
                    </Typography>
                  </>
                }
              />
            </ListItem>
            <Divider component="li" />
          </Box>
        ))}
      </List>
    );
  };

  const getPaymentsDetails = () => {
    // For simplicity, list all lockers with their paymentGenerated
    return (
      <List dense>
        {Object.entries(lockers).map(([lockerId, locker]) => (
          <Box key={lockerId}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Typography variant="subtitle1" fontWeight="bold">
                    Locker ID: {lockerId}
                  </Typography>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="textSecondary">
                      Payment Generated: ₱{locker.paymentGenerated || 0}
                    </Typography>
                  </>
                }
              />
            </ListItem>
            <Divider component="li" />
          </Box>
        ))}
      </List>
    );
  };

  const getSupportRequestsDetails = () => {
    if (keyMetrics.pendingSupportRequests === 0)
      return "No pending support requests.";

    return (
      <List dense>
        {Object.entries(supportRequests).map(([requestId, request]) => (
          <Box key={requestId}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Typography variant="subtitle1" fontWeight="bold">
                    {request.name} ({request.email})
                  </Typography>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="textSecondary">
                      Subject: {request.subject}
                    </Typography>
                    <br />
                    <Typography component="span" variant="body2" color="textSecondary">
                      Message: {request.message}
                    </Typography>
                    <br />
                    <Typography component="span" variant="body2" color="textSecondary">
                      Created At:{" "}
                      {request.createdAt
                        ? new Date(request.createdAt).toLocaleString()
                        : "N/A"}
                    </Typography>
                  </>
                }
              />
            </ListItem>
            <Divider component="li" />
          </Box>
        ))}
      </List>
    );
  };

  const getNotificationsDetails = () => {
    if (keyMetrics.activeNotifications === 0)
      return "No active notifications.";

    return (
      <List dense>
        {Object.entries(notifications).map(([notifId, notifGroup]) =>
          Object.entries(notifGroup).map(([_, notif]) => (
            <Box key={`${notifId}-${_.toString()}`}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" fontWeight="bold">
                      {notif.notifDetails}
                    </Typography>
                  }
                  secondary={`Show Time: ${
                    notif.timeToShow
                      ? new Date(notif.timeToShow).toLocaleString()
                      : "N/A"
                  }`}
                />
              </ListItem>
              <Divider component="li" />
            </Box>
          ))
        )}
      </List>
    );
  };

  // Handle Card Flip
  const handleFlip = (index) => {
    setFlippedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Loading State
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Mapping Metric Titles to Detailed Content
  const getMetricDetails = (metricTitle) => {
    switch (metricTitle) {
      case "Total Lockers":
        return (
          <Stack spacing={1}>
            <Typography variant={isMobile ? "body2" : "body1"}>
              <strong>Total Lockers:</strong> {keyMetrics.totalLockers}
            </Typography>
            <Typography variant={isMobile ? "body2" : "body1"}>
              <strong>Occupied:</strong> {keyMetrics.occupiedLockers}
            </Typography>
            <Typography variant={isMobile ? "body2" : "body1"}>
              <strong>Available:</strong> {keyMetrics.availableLockers}
            </Typography>
          </Stack>
        );
      case "Occupied":
        return getOccupiedLockersDetails();
      case "Available":
        return getAvailableLockersDetails();
      case "Total Users":
        return getTotalUsersDetails();
      case "New Users":
        return getNewUsersDetails();
      case "Overdue Fees":
        return getOverdueFeesDetails();
      case "Payments":
        return getPaymentsDetails();
      case "Support Requests":
        return getSupportRequestsDetails();
      case "Active Notifications":
        return getNotificationsDetails();
      default:
        return "No additional details.";
    }
  };

  return (
    <Box sx={{ padding: isMobile ? 1 : 2 , paddingTop: 10 }}>
      <TopBar />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={isMobile ? 1 : 2}>
        {/* Each Metric Card with Flip Effect */}
        {[
          {
            title: "Total Lockers",
            value: keyMetrics.totalLockers,
            icon: <DoorIcon fontSize="inherit" sx={{ fontSize: "5rem" }} />,
          },
          {
            title: "Occupied",
            value: keyMetrics.occupiedLockers,
            icon: <LockIcon fontSize="inherit" sx={{ fontSize: "5rem" }} />,
          },
          {
            title: "Available",
            value: keyMetrics.availableLockers,
            icon: <LockOpenIcon fontSize="inherit" sx={{ fontSize: "5rem" }} />,
          },
          {
            title: "Total Users",
            value: keyMetrics.totalUsers,
            icon: <PeopleIcon fontSize="inherit" sx={{ fontSize: "5rem" }} />,
          },
          {
            title: "New Users",
            value: keyMetrics.newUsersThisMonth,
            icon: <PersonAddIcon fontSize="inherit" sx={{ fontSize: "5rem" }} />,
          },
          {
            title: "Overdue Fees",
            value: `₱${keyMetrics.totalOverdueFees.toLocaleString()}`,
            icon: <MonetizationOnIcon fontSize="inherit" sx={{ fontSize: "5rem" }} />,
          },
          {
            title: "Payments",
            value: `₱${keyMetrics.totalPaymentsGenerated.toLocaleString()}`,
            icon: <PaymentIcon fontSize="inherit" sx={{ fontSize: "5rem" }} />,
          },
          {
            title: "Support Requests",
            value: keyMetrics.pendingSupportRequests,
            icon: <SupportIcon fontSize="inherit" sx={{ fontSize: "5rem" }} />,
          },
          {
            title: "Active Notifications",
            value: keyMetrics.activeNotifications,
            icon: <SettingsIcon fontSize="inherit" sx={{ fontSize: "5rem" }} />, // Assuming same icon, change if different
          },
        ].map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: 200, // Increased height for better mobile view
                position: "relative",
                cursor: "pointer", // Indicate that the card is interactive
                display: "flex",
                flexDirection: "column",
                borderRadius: 2,
                boxShadow: 3,
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: 6,
                },
              }}
              onClick={() => handleFlip(index)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleFlip(index);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={flippedCards[index] ? "true" : "false"}
            >
              <FlipCardContainer>
                <FlipCardInner isflipped={flippedCards[index] ? "true" : "false"}>
                  {/* Front Side */}
                  <FlipCardFront>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 2,
                      }}
                    >
                      <Box sx={{ mr: 2, color: theme.palette.primary.main }}>
                        {metric.icon}
                      </Box>
                      <Box textAlign="left">
                        <Typography
                          variant={isMobile ? "subtitle1" : "h6"}
                          color="textSecondary"
                        >
                          {metric.title}
                        </Typography>
                        <Typography
                          variant={isMobile ? "h3" : "h2"}
                          sx={{ mt: 0.5, fontWeight: "bold" }}
                        >
                          {metric.value}
                        </Typography>
                      </Box>
                    </Box>
                  </FlipCardFront>
                  {/* Back Side */}
                  <FlipCardBack>
                    {/* Close Button for Mobile Users */}
                    {isMobile && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent triggering flip
                            handleFlip(index);
                          }}
                          aria-label="Close details"
                          sx={{ color: theme.palette.primary.contrastText }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                    <Box sx={{ mt: isMobile ? 1 : 0 }}>
                      {typeof getMetricDetails(metric.title) === "string" ? (
                        <Typography
                          variant={isMobile ? "body1" : "h6"}
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {getMetricDetails(metric.title)}
                        </Typography>
                      ) : (
                        getMetricDetails(metric.title)
                      )}
                    </Box>
                  </FlipCardBack>
                </FlipCardInner>
              </FlipCardContainer>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Stats;
